import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Heart,
  DollarSign,
  Gift,
  Users,
  CalendarCheck,
  UserCheck,
  ArrowRightLeft,
  FileText,
  BarChart3,
  RotateCcw,
  RefreshCw,
  Shield,
  ClipboardList,
  CreditCard,
  AlertTriangle,
  MapPin,
  Repeat,
  Scale,
  MessageCircle,
  Globe,
  Megaphone,
} from "lucide-react";
import { ReactNode } from "react";

interface RuleCardProps {
  icon: ReactNode;
  title: string;
  children: ReactNode;
}

const RuleCard = ({ icon, title, children }: RuleCardProps) => (
  <Card className="border-border/50 shadow-sm hover:shadow-md transition-shadow">
    <CardHeader className="pb-3">
      <CardTitle className="flex items-center gap-3 text-lg">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </span>
        {title}
      </CardTitle>
    </CardHeader>
    <CardContent className="text-muted-foreground leading-relaxed">
      {children}
    </CardContent>
  </Card>
);

const Rules = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navigation />

      {/* Hero Section */}
      <section className="pt-32 pb-12 px-4">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">
            "Where Business Meets Community"
          </p>
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">
            Group Rules & Guidelines
          </h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            These rules ensure a fair, professional, and collaborative environment for all RCN members.
          </p>
        </div>
      </section>

      {/* Rules Content */}
      <section className="pb-20 px-4">
        <div className="container mx-auto max-w-4xl space-y-5">

          <RuleCard icon={<Heart className="h-5 w-5" />} title="Community Initiative Guidelines">
            <ul className="list-disc pl-5 space-y-2">
              <li>3 free memberships to be provided to 3 local non-profit organizations</li>
              <li>1 free membership to be provided to a Veteran</li>
              <li>10% of all membership dues to be provided to 3 local non-profit organizations each year, determined by leadership group and/or group vote</li>
              <li>As part of membership, members agree to volunteer at 2 service projects each year. RCN holds 2 service projects each year in which members can fulfill volunteer requirement, however, members can decide to volunteer for service projects outside of RCN if they choose, so long as the requirement to volunteer at 2 service projects per year is met</li>
            </ul>
          </RuleCard>

          <RuleCard icon={<DollarSign className="h-5 w-5" />} title="Membership: $300/year">
            <ul className="list-disc pl-5 space-y-2">
              <li>The fee secures exclusive marketing rights, as each chapter only allows one professional per industry to be represented (e.g., chapter cannot have 2 realtors)</li>
              <li>Gives them access to website where profile will be posted and where they can track their results/data</li>
              <li>Member will be given visibility and promoted on group's website and occasionally group's Facebook & LinkedIn pages</li>
              <li>10% of collective group fee to be distributed to non-profit organizations annually</li>
            </ul>
          </RuleCard>

          <RuleCard icon={<Gift className="h-5 w-5" />} title="Free Memberships">
            <ul className="list-disc pl-5 space-y-2">
              <li>3 free memberships to be provided to 3 non-profit organizations</li>
              <li>1 free membership to be provided to a Veteran</li>
              <li>1 free membership to be provided to President, VP, Website Admin during their term</li>
            </ul>
          </RuleCard>

          <RuleCard icon={<Users className="h-5 w-5" />} title="Visitor Policy">
            <ul className="list-disc pl-5 space-y-2">
              <li>Visitors can attend up to 3 meetings before membership is required.</li>
              <li>Members to not intentionally invite visitors whose profession directly conflicts with another group member's profession</li>
            </ul>
          </RuleCard>

          <RuleCard icon={<CalendarCheck className="h-5 w-5" />} title="Attendance/Participation">
            <ul className="list-disc pl-5 space-y-2">
              <li>Attendance and participation in the group is strongly encouraged. Members should attempt to send a sub if they cannot attend</li>
              <li>Consistent absences combined with inactive member participation in the group (i.e. no referrals passed, no visitors brought to meetings, no subs if not able to attend, etc..) may lead to removal from the group at the discretion of the Membership Committee & President</li>
            </ul>
          </RuleCard>

          <RuleCard icon={<UserCheck className="h-5 w-5" />} title="Subs">
            <p>Members should not deliberately arrange for someone to substitute for them if that person works in a similar industry or profession that conflicts with another member's profession within the group, unless the substitute's profession matches that of the member they are subbing for</p>
          </RuleCard>

          <RuleCard icon={<ArrowRightLeft className="h-5 w-5" />} title="Passing of Referrals/Tracking">
            <p>Members must submit referrals through the group website's "referral submission form" to ensure proper tracking. While they can also pass referrals directly to another member, the referral also must be submitted via the website for tracking purposes.</p>
          </RuleCard>

          <RuleCard icon={<BarChart3 className="h-5 w-5" />} title="Tracking Group Member Business">
            <p>Each member is responsible for submitting the number of referrals they've passed</p>
          </RuleCard>

          <RuleCard icon={<RotateCcw className="h-5 w-5" />} title="Refunds">
            <p>Pro-rated refunds are only issued to those who are removed from the group, otherwise there are no refunds of membership fee</p>
          </RuleCard>

          <RuleCard icon={<RefreshCw className="h-5 w-5" />} title="Renewals">
            <p>Notify Membership Committee within 2 weeks of expiration to request renewal. Membership Committee will then review to approve or deny renewal</p>
          </RuleCard>

          <RuleCard icon={<Shield className="h-5 w-5" />} title="Industry Representation">
            <p>Only one professional per industry allowed per group. No conflicting professionals allowed per group (e.g., cannot have 2 financial advisors in group)</p>
          </RuleCard>

          <RuleCard icon={<ClipboardList className="h-5 w-5" />} title="Membership Application Review">
            <p>Applications are reviewed in the order received.</p>
          </RuleCard>

          <RuleCard icon={<CreditCard className="h-5 w-5" />} title="Membership Payment">
            <p>Membership fee must be paid within 48 business hours of application approval</p>
          </RuleCard>

          <RuleCard icon={<AlertTriangle className="h-5 w-5" />} title="Applicant Conflicts">
            <p>Concerns about new applicants must be raised with the Membership Committee before approval</p>
          </RuleCard>

          <RuleCard icon={<MapPin className="h-5 w-5" />} title="Other Chapters">
            <p>Members may sub or visit other RCN chapters but cannot promote their business unless a spot for their profession/industry is open. Members cannot join another RCN chapter under the same industry/profession classification if they currently represent that industry/profession in another RCN chapter (unless that member is opening/starting up a new RCN chapter as President)</p>
          </RuleCard>

          <RuleCard icon={<Repeat className="h-5 w-5" />} title="Transfers">
            <p>Memberships may be transferred to another chapter if an opening exists for the same industry they represent in another chapter. No additional membership fee will be charged for a transfer</p>
          </RuleCard>

          <RuleCard icon={<Scale className="h-5 w-5" />} title="Grievances">
            <p>Members must attempt to resolve issues directly between each other; if unresolved, escalate to leadership for review.</p>
          </RuleCard>

          <RuleCard icon={<MessageCircle className="h-5 w-5" />} title="GroupMe Communication Platform">
            <p>Members should subscribe to the group's GroupMe channel where important, between meeting communication updates will be posted. Members should also respond to the meeting attendance poll that will be posted weekly.</p>
          </RuleCard>

          <RuleCard icon={<Globe className="h-5 w-5" />} title="Website">
            <p>Member must agree to not post anything inappropriate onto the website as a condition of membership</p>
          </RuleCard>

          <RuleCard icon={<Megaphone className="h-5 w-5" />} title="Inviting Visitors/Promotion of Group">
            <p>It's expected that each member will attempt to bring visitors to normally scheduled meetings to help promote the group and drive membership, whenever possible. We also ask that each group member invite at least 3 people, and commit to bringing 1 visitor to our quarterly visitor day events</p>
          </RuleCard>

        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Rules;
